import type { ReactNode, Ref } from "react";

import { useTranslation } from "react-i18next";

import type { Profile } from "@/api/bale.zod";

import { useGetProfile } from "@/api/bale";
import { Badge } from "@/components/Badge";
import { CardSubtitleHeader, CardTextContent } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { ProfileItem } from "@/components/ProfileItem";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";

export function ProfileListModal({
  ref,
  title,
  profiles,
  onClose,
}: {
  ref: Ref<HTMLDialogElement>;
  title: ReactNode;
  profiles: Profile[];
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data.event } });
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="flex max-h-200 w-lg flex-col">
      <CardSubtitleHeader>{title}</CardSubtitleHeader>
      <CardTextContent className="flex min-h-0 grow flex-col gap-2 overflow-y-auto">
        {profiles.map((profile) => (
          <div className="flex flex-row items-center gap-4" key={profile.id}>
            <ProfileItem profile={profile} />
            <div className="flex flex-row items-center gap-2">
              {me.isSuccess && profile.id === me.data.id && <Badge>{t("you")}</Badge>}
              {profile.id === event.data.account_id && <Badge>{t("host")}</Badge>}
            </div>
          </div>
        ))}
      </CardTextContent>
    </Dialog>
  );
}
